"use client";

import { readJson } from "@/app/_utils/apiJson";
import { authenticatedFetch } from "@/app/_utils/authenticatedFetch";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
  useRef,
  useSyncExternalStore,
} from "react";
// /pure: the SDK script loads when initializeTerminal calls it (payments
// shops only), not as a side effect of importing this provider.
import {
  loadStripeTerminal,
  Terminal as StripeTerminalType,
} from "@stripe/terminal-js/pure";
import { getPlatformStripePromise } from "@/app/_utils/stripeClient";
import { useUserContext } from "@/app/_hooks/useUserContext";
import { useSetAlert } from "@/app/_hooks/useAlertContext";
import {
  StripeTerminal,
  StripeTerminalLocation,
  StripeTerminalReader,
} from "../@types/stripe";
import useFeatureFlag from "@/app/_hooks/useFeatureFlag";
import { NamedUserUID } from "../@types/enums";
import {
  DeviceReaderChoice,
  formatReaderName,
  readDeviceReader,
  writeDeviceReader,
} from "@/app/_helpers/deviceReader";

// How the reader is shared between devices
// ----------------------------------------
// Every device in a shop signs in to the same account, and Stripe lets exactly
// one SDK hold a reader at a time. Stripe's fail_if_in_use only refuses a
// connect while the holder is mid card read; an idle held connection is always
// broken by an incoming connect, and the loser only finds out on its next
// command. So nothing here holds the reader between payments. A device
// connects when Process is pressed (acquireReader), takes the payment, and
// lets go (releaseReader). Whoever presses Process gets the reader; whoever is
// mid card read keeps it.
//
// Which reader a device reaches for is remembered in the browser per shop
// (deviceReader). With one registered reader none of that shows; the shop's
// stored reader is used exactly as before.

interface TerminalConnectionResult {
  error?: any;
  reader?: any;
}

export type ReaderAcquireFailure =
  | "busy"
  // This device is still on its previous reader payment (or letting go of
  // it after a cancel). Only one payment per device at a time.
  | "this_device_busy"
  | "not_found"
  | "offline"
  | "error"
  | "not_initialized"
  | "no_reader";

export interface ReaderAcquireResult {
  reader?: any;
  error?: any;
  failure?: ReaderAcquireFailure;
  readerLabel?: string;
}

export interface ResolvedReader {
  serial: string;
  label: string;
  locationId: string | null;
  // Where the choice came from: this device's saved pick, or the only reader
  // on the account.
  source: "device" | "only";
}

// The payment lock: on from the moment Process is pressed until the charged
// payment is saved. Payment modals read it (PaymentModal) to refuse closing.
// A tiny store rather than context so the jobs board rows, which each carry a
// Take Payment modal, never re-render for it.
let paymentLockHolders = 0;
const paymentLockListeners = new Set<() => void>();
const changePaymentLock = (delta: number) => {
  paymentLockHolders = Math.max(0, paymentLockHolders + delta);
  paymentLockListeners.forEach((listener) => listener());
};
const subscribePaymentLock = (listener: () => void) => {
  paymentLockListeners.add(listener);
  return () => {
    paymentLockListeners.delete(listener);
  };
};

// True while any payment on this device is under way or being saved.
export const useReaderLocked = () =>
  useSyncExternalStore(
    subscribePaymentLock,
    () => paymentLockHolders > 0,
    () => false
  );

// Holds the payment lock while `active` is true.
export const useHoldReaderLock = (active: boolean) => {
  useEffect(() => {
    if (!active) return;
    changePaymentLock(1);
    return () => changePaymentLock(-1);
  }, [active]);
};

// Upper bounds on the two network steps of taking a reader. Without them a
// hung discovery or connect (bad DNS, a blocked local network) leaves the
// payment screen spinning forever. Generous on purpose: a healthy reader on
// slow shop WiFi connects in a few seconds.
const DISCOVER_TIMEOUT_MS = 20_000;
const CONNECT_TIMEOUT_MS = 30_000;

// How long a reader this device just connected to shows Ready, whatever
// Stripe's lagging status says.
const REACHED_TRUST_MS = 10 * 60_000;
const withReachedStatus = (
  readers: StripeTerminalReader[],
  reached: Map<string, number>
): StripeTerminalReader[] => {
  const now = Date.now();
  return readers.map((reader) => {
    const at = reached.get(reader.serial_number);
    return at && now - at < REACHED_TRUST_MS && reader.status !== "online"
      ? { ...reader, status: "online" }
      : reader;
  });
};
const TIMED_OUT = { timedOut: true } as const;
const isTimedOut = (value: unknown): value is typeof TIMED_OUT =>
  value === TIMED_OUT;
const withTimeout = <T,>(
  promise: Promise<T>,
  ms: number
): Promise<T | typeof TIMED_OUT> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => resolve(TIMED_OUT), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });

// A fail_if_in_use rejection. Confirmed on a WisePOS E on 1 Nov 2026 with two
// browsers (one mid card read): the SDK returns
//   { code: "reader_error", message: "Reader is currently in use." }
// reader_error is generic (any reader-side failure), so the message is what
// tells busy apart. The looser "in use" match is a fallback in case Stripe
// rewords it. "Busy" alone is not matched: a reader busy updating is not
// another device's payment.
export const isReaderBusyError = (error: any): boolean => {
  if (!error) return false;
  const code = String(error.code || "").toLowerCase();
  const message = String(error.message || "").toLowerCase();
  if (code === "reader_error" && message.includes("currently in use")) {
    return true;
  }
  return /\bin use\b/.test(message);
};

export const readerFailureMessage = (
  failure: ReaderAcquireFailure | undefined,
  rawLabel?: string
): string => {
  // Names arrive raw from the SDK's discovery; show them as staff see them.
  const readerLabel = rawLabel ? formatReaderName(rawLabel) : undefined;
  const label = readerLabel || "The card reader";
  switch (failure) {
    case "busy":
      return "Reader busy on another device. Wait for that payment to finish, or press Cancel on the reader.";
    case "this_device_busy":
      return "This device is already using the card reader. Wait a moment and try again.";
    case "not_found":
      // Discovery did not return this reader. Usually it was removed from
      // the account, but a reader can still be registered and not be
      // reachable from a browser (Stripe's simulated reader, for one), so
      // the wording only says what this device saw.
      return readerLabel
        ? `This device could not find ${readerLabel}.`
        : "No card reader is registered to this shop.";
    case "offline":
      return `${label} is offline. Check its power and WiFi.`;
    case "not_initialized":
      return "The card reader is not ready on this device. Reload the page and try again.";
    case "no_reader":
      return "No card reader is set up for this shop.";
    default:
      return `Could not connect to ${label}. This device must be on the same WiFi as the reader.`;
  }
};

const getLocalIPs = (timeoutMs = 1500): Promise<string[]> =>
  new Promise((resolve) => {
    const ips = new Set<string>();
    let pc: RTCPeerConnection | null = null;

    const cleanup = () => {
      try {
        pc?.close();
      } catch {}
      pc = null;
    };

    const timer = setTimeout(() => {
      cleanup();
      resolve(Array.from(ips));
    }, timeoutMs);

    try {
      if (typeof RTCPeerConnection === "undefined") {
        clearTimeout(timer);
        resolve([]);
        return;
      }
      pc = new RTCPeerConnection({ iceServers: [] });
      pc.createDataChannel("");
      pc.onicecandidate = (e) => {
        if (!e.candidate) {
          clearTimeout(timer);
          cleanup();
          resolve(Array.from(ips));
          return;
        }
        const match = /([0-9]{1,3}(\.[0-9]{1,3}){3})/.exec(
          e.candidate.candidate
        );
        if (match) ips.add(match[1]);
      };
      pc.createOffer()
        .then((offer) => pc?.setLocalDescription(offer))
        .catch(() => {
          clearTimeout(timer);
          cleanup();
          resolve(Array.from(ips));
        });
    } catch {
      clearTimeout(timer);
      cleanup();
      resolve(Array.from(ips));
    }
  });

// Reader failures are emailed to contact@ (via /api/log/terminal-error).
// Every shop reports for the first week after the multi reader release
// (deployed 3 Nov 2026), so a break shows up there before a ticket does.
// After that only the accounts under active investigation below report.
// "always" reports (a charged card missing from the job) ignore both.
const REPORT_EVERY_SHOP_UNTIL = Date.parse("2026-11-10T00:00:00Z");
// One email per shop, stage and reader every few minutes, so a reader that
// is off and Process pressed ten times sends one.
const REPORT_THROTTLE_MS = 5 * 60_000;
const lastReportAt = new Map<string, number>();
const TERMINAL_ERROR_REPORT_UIDS = new Set<string>([
  // TrueLife, S710 won't connect from their second computer
  NamedUserUID.TRUELIFE,
  // Critters Creation, S710 drops between laptop and iPad
  NamedUserUID.CRITTERS,
]);

export const reportTerminalError = async (
  payload: {
    userId: string;
    stage: string;
    errorMessage?: string;
    errorCode?: string | number;
    errorType?: string;
    readerSerial?: string;
    readerLabel?: string;
    readerStatus?: string;
    details?: Record<string, unknown>;
  },
  options?: { always?: boolean }
) => {
  if (!options?.always) {
    const watched =
      TERMINAL_ERROR_REPORT_UIDS.has(payload.userId) ||
      Date.now() < REPORT_EVERY_SHOP_UNTIL;
    if (!watched) return;
    const key = `${payload.userId}:${payload.stage}:${
      payload.readerSerial ?? ""
    }`;
    if (Date.now() - (lastReportAt.get(key) ?? 0) < REPORT_THROTTLE_MS) {
      return;
    }
    lastReportAt.set(key, Date.now());
  }

  try {
    let localIps: string[] = [];
    try {
      localIps = await getLocalIPs(1500);
    } catch {}

    const fullPayload = {
      ...payload,
      userAgent:
        typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
      localIps,
      timestamp: new Date().toISOString(),
    };

    fetch("/api/log/terminal-error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(fullPayload),
      keepalive: true,
    }).catch((e) => console.warn("reportTerminalError failed", e));
  } catch (e) {
    console.warn("reportTerminalError threw", e);
  }
};

type TerminalDataState =
  | (StripeTerminal & {
      readers?: StripeTerminalReader[];
      locations?: StripeTerminalLocation[];
    })
  | null;

// Set by the payment screen when staff press Cancel during the connect.
export interface AcquireCancel {
  cancelled: boolean;
}

// The connect in flight on this device. The SDK cannot stop a discover or
// connect once started, so a cancelled one runs on until it settles.
interface PendingAcquire {
  serial: string;
  cancel: AcquireCancel;
  // A cancelled attempt that connected and is letting the reader go again.
  closing: boolean;
  promise: Promise<ReaderAcquireResult> | null;
}

interface StripeTerminalContextType {
  terminal: StripeTerminalType | null;
  isTerminalInitialized: boolean;
  // The only way to touch the reader: take it for one payment or one test,
  // then release. Silent. Hosts use useTerminalPayment, not these directly.
  acquireReader: (options?: {
    serial?: string;
    failIfInUse?: boolean;
    cancel?: AcquireCancel;
  }) => Promise<ReaderAcquireResult>;
  releaseReader: () => Promise<void>;
  terminalData: TerminalDataState;
  // Live from Stripe via the list route.
  readers: StripeTerminalReader[];
  locations: StripeTerminalLocation[];
  hasMultipleReaders: boolean;
  hasMultipleLocations: boolean;
  // This device's saved pick, and what Process will reach for.
  deviceReader: DeviceReaderChoice | null;
  setDeviceReader: (choice: DeviceReaderChoice | null) => void;
  resolvedReader: ResolvedReader | null;
  // More than one reader and this device has not picked one that is still
  // registered. Payment screens show the picker and refuse to charge.
  mustPickReader: boolean;
  registerReader: (
    registrationCode: string,
    label?: string,
    locationId?: string
  ) => Promise<any>;
  removeReader: (readerId: string) => Promise<void>;
  renameReader: (readerId: string, label: string) => Promise<void>;
  createLocation: (
    displayName: string,
    address: StripeTerminalLocation["address"]
  ) => Promise<StripeTerminalLocation>;
  // withLocations: also fetch every location (Settings). Payment screens get
  // locations only when the shop's readers span more than one site.
  refreshReaderStatus: (options?: {
    withLocations?: boolean;
    maxAgeMs?: number;
  }) => Promise<void>;
  ensureLocation: () => Promise<any>;
  isEnsuringLocation: boolean;
  isRegistering: boolean;
  terminalError: string | null;
}

const StripeTerminalContext = createContext<
  StripeTerminalContextType | undefined
>(undefined);

interface StripeTerminalProviderProps {
  children: ReactNode;
}

export const StripeTerminalProvider = ({
  children,
}: StripeTerminalProviderProps) => {
  const user = useUserContext();
  const addAlert = useSetAlert();

  // Feature flag check
  const paymentsEnabled = useFeatureFlag("MM_PAYMENTS_ENABLED");

  // The flag alone is not enough to start talking to Stripe: it bypasses its
  // own user restriction, so it is true for every signed-in account. The
  // account's own onboarding state is what decides whether a terminal exists.
  // Without this, accounts that never set up payments still fire a reader
  // status request on every page load and log the error it comes back with.
  const terminalConfigured = Boolean(
    paymentsEnabled &&
      user?.settings?.mmPaymentsConnected &&
      user?.settings?.mmPaymentsEnabled
  );

  // Payments shops keep Stripe.js loaded app-wide, as they always have (its
  // fraud signals want it on every page, and the first payment form opens
  // without waiting on the script). Every other shop never loads it.
  const stripeJsWanted = Boolean(
    paymentsEnabled && user?.settings?.mmPaymentsEnabled
  );
  useEffect(() => {
    if (stripeJsWanted) getPlatformStripePromise();
  }, [stripeJsWanted]);

  // Terminal state
  const [terminal, setTerminal] = useState<any | null>(null);
  const [isTerminalInitialized, setIsTerminalInitialized] = useState(false);
  const [terminalData, setTerminalData] = useState<TerminalDataState>(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [terminalError, setTerminalError] = useState<string | null>(null);
  const [isEnsuringLocation, setIsEnsuringLocation] = useState(false);
  const [deviceReader, setDeviceReaderState] =
    useState<DeviceReaderChoice | null>(null);

  // Refs to prevent re-initialization
  const terminalInitializationAttempted = useRef(false);
  // The SDK captures handleUnexpectedDisconnect once at create time, so it
  // reads reader details through refs rather than a stale closure.
  const terminalDataRef = useRef<TerminalDataState>(null);
  const connectedReaderRef = useRef<any | null>(null);
  useEffect(() => {
    terminalDataRef.current = terminalData;
  }, [terminalData]);
  // True from the start of acquireReader until releaseReader. One reader
  // payment per device at a time: a second Process on this device (another
  // tab of the POS modal, say) must not disconnect the first mid card read.
  const holdRef = useRef(false);
  // Bumped on every acquire, so a connect that lands after it timed out only
  // lets go of the reader if no newer payment has started since.
  const acquireGenRef = useRef(0);
  const pendingAcquireRef = useRef<PendingAcquire | null>(null);
  // Whose reader list terminalData holds. Switching shops (impersonation)
  // must never show or act on the previous shop's readers.
  const uidRef = useRef<string | undefined>(user?.uid);
  uidRef.current = user?.uid;
  // Readers this device has just talked to, by serial, with when. Stripe's
  // status can say Offline for minutes after a reader comes back, but a
  // reader we just connected to is plainly on, so it shows Ready for a while
  // whatever the list says. Cleared when a connect to it fails.
  const reachedRef = useRef<Map<string, number>>(new Map());
  // When the reader list last arrived, so popups opening back to back do not
  // each ask Stripe again.
  const lastRefreshRef = useRef(0);

  const readers = useMemo(() => terminalData?.readers ?? [], [terminalData]);
  const locations = useMemo(
    () => terminalData?.locations ?? [],
    [terminalData]
  );
  const hasMultipleReaders = readers.length > 1;
  const hasMultipleLocations = locations.length > 1;

  // This device's saved reader, per shop. A new shop starts with no reader
  // list until its own arrives.
  useEffect(() => {
    setDeviceReaderState(readDeviceReader(user?.uid));
    setTerminalData(null);
    reachedRef.current = new Map();
    lastRefreshRef.current = 0;
  }, [user?.uid]);

  const setDeviceReader = useCallback(
    (choice: DeviceReaderChoice | null) => {
      writeDeviceReader(user?.uid, choice);
      setDeviceReaderState(choice);
    },
    [user?.uid]
  );

  // The saved pick counts only while Stripe still lists that reader. It is
  // never deleted automatically: a removed reader just stops resolving, and
  // the device is asked again. Nothing here can wipe a saved pick by
  // comparing it against a list that has not loaded, or belongs to another
  // shop.
  const pickIsLive = Boolean(
    deviceReader &&
      readers.some((reader) => reader.serial_number === deviceReader.serial)
  );
  const mustPickReader = hasMultipleReaders && !pickIsLive;

  // What Process reaches for on this device: its own pick, else the only
  // reader on the account. A shop with more than one reader always picks per
  // device, so a charge never lands on a reader at another site by default.
  const resolvedReader = useMemo<ResolvedReader | null>(() => {
    if (deviceReader && pickIsLive) {
      return {
        serial: deviceReader.serial,
        label: formatReaderName(deviceReader.label),
        locationId: deviceReader.locationId,
        source: "device",
      };
    }
    if (readers.length === 1) {
      const only = readers[0];
      return {
        serial: only.serial_number,
        label: only.label,
        locationId: only.location,
        source: "only",
      };
    }
    return null;
  }, [deviceReader, pickIsLive, readers]);
  const resolvedReaderRef = useRef<ResolvedReader | null>(null);
  resolvedReaderRef.current = resolvedReader;

  // Terminal: Fetch connection token
  const fetchConnectionToken = useCallback(async () => {
    if (!user?.uid) {
      throw new Error("User not authenticated");
    }

    const response = await authenticatedFetch(
      "/api/stripe/terminal/connection-token",
      { method: "POST" }
    );

    if (!response.ok) {
      const errorData = await readJson(response);
      // Without a token no reader works on this device at all.
      reportTerminalError({
        userId: user.uid,
        stage: "connection-token",
        errorMessage: errorData.error || "Failed to fetch connection token",
        errorCode: response.status,
      });
      throw new Error(errorData.error || "Failed to fetch connection token");
    }

    const data = await readJson(response);
    return data.secret;
  }, [user?.uid]);

  // Terminal: Handle unexpected reader disconnect
  // The SDK passes { error: { message } } here after a failed heartbeat
  // (every 60s) or a failed reader command. That message is the only record
  // of why the reader dropped, so it goes to the error report.
  //
  // No alert and no Firestore write. Under the connect-per-payment model this
  // fires in two cases: another device took the reader (benign, nobody was
  // using it here, or the in-flight collect fails and the form shows that),
  // or the reader dropped off the network mid payment (the collect fails and
  // the form shows that). Neither needs a toast on top.
  const handleUnexpectedDisconnect = useCallback(
    async (event?: { error?: { message?: string } }) => {
      if (!user?.uid) return;

      reportTerminalError({
        userId: user.uid,
        stage: "unexpectedReaderDisconnect",
        errorMessage: event?.error?.message,
        readerSerial: connectedReaderRef.current?.serial_number,
        readerLabel: connectedReaderRef.current?.label,
        readerStatus: connectedReaderRef.current?.status,
      });

      connectedReaderRef.current = null;
    },
    [user?.uid]
  );

  // Terminal: Initialize Terminal SDK
  const initializeTerminal = useCallback(async () => {
    if (terminalInitializationAttempted.current || !user?.uid) return;

    // Check if user has Stripe payments connected before initializing terminal
    if (
      !user.settings?.mmPaymentsConnected ||
      !user.settings?.mmPaymentsEnabled
    ) {
      return;
    }

    terminalInitializationAttempted.current = true;

    try {
      // Load Stripe Terminal using the npm package
      const StripeTerminal = await loadStripeTerminal();

      if (!StripeTerminal) {
        throw new Error("Failed to load Stripe Terminal SDK");
      }

      // Create Terminal instance
      const terminalInstance = StripeTerminal.create({
        onFetchConnectionToken: fetchConnectionToken,
        onUnexpectedReaderDisconnect: handleUnexpectedDisconnect,
        readerBehavior: {
          allowCustomerCancel: true,
        },
      });

      setTerminal(terminalInstance);
      setIsTerminalInitialized(true);
      setTerminalError(null);
    } catch (err: any) {
      console.error("Failed to initialize Stripe Terminal:", err);
      setTerminalError(err.message || "Failed to initialize terminal");
      addAlert({
        type: "error",
        title: "Terminal Initialization Failed",
        content: err.message || "Failed to initialize Stripe Terminal",
      });
    }
  }, [
    user?.uid,
    user?.settings,
    fetchConnectionToken,
    handleUnexpectedDisconnect,
    addAlert,
  ]);

  // Connect step. Drops whatever this SDK still holds first (the SDK refuses
  // a second connect with already_connected, and a session another device
  // broke can still read "connected" until its next command), then connects
  // with the busy guard on unless told otherwise.
  const connectToReader = useCallback(
    async (
      reader: any,
      failIfInUse: boolean
    ): Promise<TerminalConnectionResult> => {
      if (terminal.getConnectionStatus?.() !== "not_connected") {
        try {
          await terminal.disconnectReader();
        } catch {}
        connectedReaderRef.current = null;
      }
      return terminal.connectReader(reader, { fail_if_in_use: failIfInUse });
    },
    [terminal]
  );

  const logConnectFailure = useCallback(
    (stage: string, reader: any, error: any) => {
      if (user?.uid === NamedUserUID.ADMIN) {
        // Left in on purpose: this is how the busy rejection's real code and
        // message get read off the console during the two browser test.
        console.warn(`[terminal] ${stage} failed`, {
          code: error?.code,
          type: error?.type,
          message: error?.message,
          reader: reader?.label,
          readerStatus: reader?.status,
        });
      }
      // Busy is two devices reaching for one reader: expected, not broken.
      if (user?.uid && !isReaderBusyError(error)) {
        reportTerminalError({
          userId: user.uid,
          stage,
          errorMessage: error?.message,
          errorCode: error?.code,
          errorType: error?.type,
          readerSerial: reader?.serial_number,
          readerLabel: reader?.label,
          readerStatus: reader?.status,
        });
      }
    },
    [user?.uid]
  );

  // Fetch the shop's readers (and their Stripe status) from the list route.
  // maxAgeMs skips the call when the list arrived that recently, so popups
  // can ask on open without stacking requests.
  const refreshReaderStatus = useCallback(
    async (options?: { withLocations?: boolean; maxAgeMs?: number }) => {
      const uid = user?.uid;
      if (!uid) return;
      if (
        options?.maxAgeMs &&
        Date.now() - lastRefreshRef.current < options.maxAgeMs
      ) {
        return;
      }

      try {
        const response = await authenticatedFetch(
          `/api/stripe/terminal/list${
            options?.withLocations ? "?locations=1" : ""
          }`
        );

        if (!response.ok) {
          const errorData = await readJson(response);
          throw new Error(errorData.error || "Failed to fetch reader status");
        }

        const data = await readJson(response);
        // The shop changed while this was in flight: it is the old shop's.
        if (uidRef.current !== uid) return;
        lastRefreshRef.current = Date.now();
        setTerminalData((prev) => ({
          ...data.currentReader,
          readers: withReachedStatus(data.readers ?? [], reachedRef.current),
          // The route leaves locations out when it skipped fetching them;
          // keep the last list rather than blanking Settings.
          locations: data.locations ?? prev?.locations ?? [],
        }));
        setTerminalError(null);
      } catch (err: any) {
        if (uidRef.current !== uid) return;
        console.error("Failed to refresh reader status:", err);
        setTerminalError(err.message);
      }
    },
    [user?.uid]
  );

  // Record whether this device just reached a reader. Reached: it shows
  // Ready now and through Stripe's lag. Not reached: drop that, and ask
  // Stripe again so the badge catches up with whatever went wrong.
  const noteReaderReached = useCallback(
    (serial: string | undefined, reached: boolean) => {
      if (!serial) return;
      if (reached) {
        reachedRef.current.set(serial, Date.now());
        setTerminalData((prev) =>
          prev
            ? {
                ...prev,
                readers: withReachedStatus(
                  prev.readers ?? [],
                  reachedRef.current
                ),
              }
            : prev
        );
      } else {
        reachedRef.current.delete(serial);
        refreshReaderStatus();
      }
    },
    [refreshReaderStatus]
  );

  // Take the reader for one payment or one Settings test. Silent: no alerts,
  // no Firestore writes. This is the only way anything connects, and every
  // caller releases straight after, so nothing holds the reader between
  // payments. Discovery is always fresh (Stripe says never to reuse a Reader
  // object from an earlier discovery). The connect is attempted whatever
  // Stripe's status field says, because that field lags and the docs say not
  // to gate payments on it; the status only shapes the message on failure.
  const acquireReader = useCallback(
    async (options?: {
      serial?: string;
      failIfInUse?: boolean;
      cancel?: AcquireCancel;
    }): Promise<ReaderAcquireResult> => {
      if (!terminal || !isTerminalInitialized) {
        return {
          failure: "not_initialized",
          error: new Error("Terminal not initialized"),
        };
      }

      const listed = options?.serial
        ? terminalDataRef.current?.readers?.find(
            (r) => r.serial_number === options.serial
          )
        : undefined;
      const target = options?.serial
        ? { serial: options.serial, label: listed?.label ?? "" }
        : resolvedReaderRef.current;
      if (!target) {
        return { failure: "no_reader", error: new Error("No reader chosen") };
      }

      const cancel = options?.cancel ?? { cancelled: false };
      const cancelledResult = (): ReaderAcquireResult => ({
        failure: "error",
        error: new Error("Cancelled"),
      });

      // Staff pressed Cancel during a connect, then Process again. The old
      // attempt is still running: for the same reader it is picked back up
      // rather than refusing with "already using the reader"; for another
      // reader this waits until the old one has let go.
      const pending = pendingAcquireRef.current;
      if (holdRef.current && pending?.promise && pending.cancel.cancelled) {
        if (pending.serial === target.serial && !pending.closing) {
          pending.cancel = cancel;
          return pending.promise;
        }
        await pending.promise.catch(() => undefined);
        if (cancel.cancelled) return cancelledResult();
      }

      if (holdRef.current) {
        return {
          failure: "this_device_busy",
          error: new Error("This device is already using the reader"),
        };
      }

      const failIfInUse = options?.failIfInUse ?? true;

      holdRef.current = true;
      const gen = ++acquireGenRef.current;
      const entry: PendingAcquire = {
        serial: target.serial,
        cancel,
        closing: false,
        promise: null,
      };

      const attempt = async (): Promise<ReaderAcquireResult> => {
        let held = false;

        try {
          const discoverResult = await withTimeout<any>(
            terminal.discoverReaders({ simulated: false }),
            DISCOVER_TIMEOUT_MS
          );

          if (isTimedOut(discoverResult) || discoverResult.error) {
            const error = isTimedOut(discoverResult)
              ? { message: "Reader discovery timed out" }
              : discoverResult.error;
            logConnectFailure("acquire-discover", null, error);
            return {
              failure: "error",
              error,
              readerLabel: target.label || undefined,
            };
          }

          const discovered = discoverResult.discoveredReaders || [];
          const reader = discovered.find(
            (candidate: any) => candidate.serial_number === target.serial
          );

          // Cancelled while finding the reader: do not connect at all.
          if (entry.cancel.cancelled) return cancelledResult();

          if (!reader) {
            // Stripe no longer lists this reader. The picker explains it; the
            // saved pick stops resolving on the next list refresh.
            noteReaderReached(target.serial, false);
            return {
              failure: "not_found",
              error: new Error("Reader not found"),
              readerLabel: target.label || undefined,
            };
          }

          const connectPromise = connectToReader(reader, failIfInUse);
          const connectOutcome = await withTimeout(
            connectPromise,
            CONNECT_TIMEOUT_MS
          );

          if (isTimedOut(connectOutcome)) {
            // Gave up waiting. If the connect lands after all, let the reader
            // go again, unless a newer payment on this device has started.
            connectPromise
              .then((late) => {
                if (
                  !late?.error &&
                  acquireGenRef.current === gen &&
                  !holdRef.current
                ) {
                  terminal.disconnectReader().catch(() => {});
                }
              })
              .catch(() => {});
            logConnectFailure("acquire-connect-timeout", reader, {
              message: "Reader connect timed out",
            });
            noteReaderReached(target.serial, false);
            return {
              failure: reader.status === "offline" ? "offline" : "error",
              error: new Error("Reader connect timed out"),
              readerLabel: reader.label,
            };
          }

          const connectResult = connectOutcome as TerminalConnectionResult;
          if (connectResult.error) {
            logConnectFailure("acquire-connect", reader, connectResult.error);
            const failure: ReaderAcquireFailure = isReaderBusyError(
              connectResult.error
            )
              ? "busy"
              : reader.status === "offline"
              ? "offline"
              : "error";
            // Busy means another device is on it, so it is on.
            noteReaderReached(target.serial, failure === "busy");
            return {
              failure,
              error: connectResult.error,
              readerLabel: reader.label,
            };
          }

          noteReaderReached(target.serial, true);

          // Connected after staff cancelled, and nobody picked it back up:
          // let the reader go again.
          if (entry.cancel.cancelled) {
            entry.closing = true;
            await terminal.disconnectReader().catch(() => {});
            return cancelledResult();
          }

          connectedReaderRef.current = connectResult.reader;
          held = true;
          return { reader: connectResult.reader };
        } catch (err: any) {
          logConnectFailure("acquire-throw", null, err);
          return {
            failure: "error",
            error: err,
            readerLabel: target.label || undefined,
          };
        } finally {
          if (!held) holdRef.current = false;
        }
      };

      entry.promise = attempt();
      pendingAcquireRef.current = entry;
      const clear = () => {
        if (pendingAcquireRef.current === entry) {
          pendingAcquireRef.current = null;
        }
      };
      entry.promise.then(clear, clear);
      return entry.promise;
    },
    [
      terminal,
      isTerminalInitialized,
      connectToReader,
      logConnectFailure,
      noteReaderReached,
    ]
  );

  // Payment flows: let the reader go after the payment. Silent.
  const releaseReader = useCallback(async () => {
    if (!terminal) return;
    try {
      if (terminal.getConnectionStatus?.() !== "not_connected") {
        await terminal.disconnectReader();
      }
    } catch (err) {
      // The SDK already dropped it (another device took over, or the reader
      // went away). Nothing to hold on to either way.
      console.warn("releaseReader", err);
    } finally {
      connectedReaderRef.current = null;
      holdRef.current = false;
    }
  }, [terminal]);

  // Terminal: Register a new reader
  const registerReader = useCallback(
    async (registrationCode: string, label?: string, locationId?: string) => {
      if (!user?.uid) {
        throw new Error("User not authenticated");
      }

      setIsRegistering(true);
      setTerminalError(null);

      try {
        const response = await authenticatedFetch("/api/stripe/terminal/register", {
          method: "POST",
          body: JSON.stringify({
            registrationCode,
            label,
            locationId,
          }),
        });

        if (!response.ok) {
          const errorData = await readJson(response);
          throw new Error(errorData.error || "Failed to register reader");
        }

        const data = await readJson(response);

        addAlert({
          type: "success",
          title: "Reader Registered",
          content: `Successfully registered ${data.reader.label}`,
        });

        // Refresh terminal data
        await refreshReaderStatus();

        return data.reader;
      } catch (err: any) {
        console.error("Reader registration failed:", err);
        setTerminalError(err.message);
        addAlert({
          type: "error",
          title: "Registration Failed",
          content: err.message || "Failed to register reader",
        });
        throw err;
      } finally {
        setIsRegistering(false);
      }
    },
    [user?.uid, addAlert, refreshReaderStatus]
  );

  // Terminal: Remove a reader from the Stripe account
  const removeReader = useCallback(
    async (readerId: string) => {
      const response = await authenticatedFetch("/api/stripe/terminal/remove", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ readerId }),
      });
      if (!response.ok) {
        const errorData = await readJson(response);
        throw new Error(errorData.error || "Failed to remove reader");
      }
      const removed = readers.find((r) => r.id === readerId);
      if (removed && deviceReader?.serial === removed.serial_number) {
        setDeviceReader(null);
      }
      await refreshReaderStatus();
    },
    [readers, deviceReader, setDeviceReader, refreshReaderStatus]
  );

  // Terminal: Rename a reader
  const renameReader = useCallback(
    async (readerId: string, label: string) => {
      const response = await authenticatedFetch("/api/stripe/terminal/rename", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ readerId, label }),
      });
      if (!response.ok) {
        const errorData = await readJson(response);
        throw new Error(errorData.error || "Failed to rename reader");
      }
      const renamed = readers.find((r) => r.id === readerId);
      if (renamed && deviceReader?.serial === renamed.serial_number) {
        setDeviceReader({ ...deviceReader, label });
      }
      await refreshReaderStatus();
    },
    [readers, deviceReader, setDeviceReader, refreshReaderStatus]
  );

  // Terminal: Add a location for a second site
  const createLocation = useCallback(
    async (
      displayName: string,
      address: StripeTerminalLocation["address"]
    ): Promise<StripeTerminalLocation> => {
      const response = await authenticatedFetch(
        "/api/stripe/terminal/locations",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ displayName, address }),
        }
      );
      if (!response.ok) {
        const errorData = await readJson(response);
        throw new Error(errorData.error || "Failed to create location");
      }
      const data = await readJson(response);
      await refreshReaderStatus({ withLocations: true });
      return data.location;
    },
    [refreshReaderStatus]
  );

  // Terminal: Ensure location exists
  const ensureLocation = useCallback(async () => {
    if (!user?.uid) {
      throw new Error("User not authenticated");
    }
    setIsEnsuringLocation(true);

    try {
      const response = await authenticatedFetch(
        "/api/stripe/terminal/ensure-location",
        { method: "POST" }
      );

      if (!response.ok) {
        const errorData = await readJson(response);
        throw new Error(errorData.error || "Failed to create location");
      }

      const data = await readJson(response);

      return data;
    } catch (err: any) {
      console.error("Location creation failed:", err);
      setTerminalError(err.message);
      throw err;
    } finally {
      setIsEnsuringLocation(false);
    }
  }, [user?.uid]);

  // Terminal: Initialize on mount
  useEffect(() => {
    if (
      terminalConfigured &&
      user?.uid &&
      !isTerminalInitialized &&
      !terminalInitializationAttempted.current
    ) {
      initializeTerminal();
    }
  }, [
    terminalConfigured,
    user?.uid,
    isTerminalInitialized,
    initializeTerminal,
  ]);

  // Terminal: Refresh reader status on mount
  useEffect(() => {
    if (terminalConfigured && user?.uid) {
      refreshReaderStatus();
    }
  }, [terminalConfigured, user?.uid, refreshReaderStatus]);

  const contextValue = useMemo<StripeTerminalContextType>(
    () => ({
      terminal,
      isTerminalInitialized,
      acquireReader,
      releaseReader,
      terminalData,
      readers,
      locations,
      hasMultipleReaders,
      hasMultipleLocations,
      deviceReader,
      setDeviceReader,
      resolvedReader,
      mustPickReader,
      registerReader,
      removeReader,
      renameReader,
      createLocation,
      refreshReaderStatus,
      ensureLocation,
      isEnsuringLocation,
      isRegistering,
      terminalError,
    }),
    [
      terminal,
      isTerminalInitialized,
      acquireReader,
      releaseReader,
      terminalData,
      readers,
      locations,
      hasMultipleReaders,
      hasMultipleLocations,
      deviceReader,
      setDeviceReader,
      resolvedReader,
      mustPickReader,
      registerReader,
      removeReader,
      renameReader,
      createLocation,
      refreshReaderStatus,
      ensureLocation,
      isEnsuringLocation,
      isRegistering,
      terminalError,
    ]
  );

  return (
    <StripeTerminalContext.Provider
      value={paymentsEnabled ? contextValue : STUB_CONTEXT}
    >
      {children}
    </StripeTerminalContext.Provider>
  );
};

// Payments not enabled for this account: nothing to reach for.
const STUB_CONTEXT: StripeTerminalContextType = {
  terminal: null,
  isTerminalInitialized: false,
  acquireReader: async () => ({ failure: "not_initialized" }),
  releaseReader: async () => {},
  terminalData: null,
  readers: [],
  locations: [],
  hasMultipleReaders: false,
  hasMultipleLocations: false,
  deviceReader: null,
  setDeviceReader: () => {},
  resolvedReader: null,
  mustPickReader: false,
  registerReader: async () => {},
  removeReader: async () => {},
  renameReader: async () => {},
  createLocation: async () => {
    throw new Error("Payments not enabled");
  },
  refreshReaderStatus: async () => {},
  ensureLocation: async () => {},
  isEnsuringLocation: false,
  isRegistering: false,
  terminalError: null,
};

export const useStripeTerminalContext = () => {
  const context = useContext(StripeTerminalContext);
  if (context === undefined) {
    throw new Error(
      "useStripeTerminalContext must be used within a StripeTerminalProvider"
    );
  }
  return context;
};
